import { useState } from 'react';
import { Plus } from 'lucide-react';
import { DataListCard, type DataListColumn, type DataListAction } from '@/components/common/DataListCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useJobsByLead } from '../hooks/useJobsByLead';
import { CreateJobDialog } from './CreateJobDialog';
import type { Lead } from '@/features/leads/types';

interface LeadJobsPanelProps {
  lead: Lead;
}

interface JobSummary {
  id: number;
  jobNumber: string;
  status: string;
  startDate: string;
  propertyAddress: string;
  assignedTo?: string;
  totalCharges: number;
  profit: number;
}

export function LeadJobsPanel({ lead }: LeadJobsPanelProps) {
  const { data: jobs, isLoading, error, refetch } = useJobsByLead(lead.id);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };


  const columns: DataListColumn<JobSummary>[] = [
    {
      key: 'jobNumber',
      label: 'Job Number',
      render: (job) => <span className="font-semibold">{job.jobNumber}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (job) => <span className="font-semibold">{job.status}</span>,
    },
    {
      key: 'propertyAddress',
      label: 'Property Address',
      className: 'overflow-hidden',
      width: '2fr',
      render: (job) => (
        <span className="block truncate" title={job.propertyAddress}>
          {job.propertyAddress}
        </span>
      ),
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      render: (job) => <span>{job.assignedTo ?? '—'}</span>,
    },
    {
      key: 'totalCharges',
      label: 'Total Charges',
      render: (job) => <span className="font-semibold">{formatCurrency(job.totalCharges)}</span>,
    },
    {
      key: 'profit',
      label: 'Profit',
      render: (job) => (
        <span className={`font-semibold ${job.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {formatCurrency(job.profit)}
        </span>
      ),
    },
  ];

  const actions: DataListAction<JobSummary>[] = [
    {
      label: 'View',
      href: (job) => `/jobs/${job.id}`,
    },
  ];

  const canCreateJob = lead.status === 'Sold';

  return (
    <div className="bg-card rounded-[20px] px-5 py-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-xl font-semibold text-secondary">Jobs</h3>
          {!isLoading && (
            <Badge variant="secondary" className="rounded-full">
              {jobs.length}
            </Badge>
          )}
        </div>
        {canCreateJob && (
          <Button
            variant="outline"
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <Plus className="h-4 w-4 text-[#DC2626]" />
            Create Job
          </Button>
        )}
      </div>
      <DataListCard
        items={jobs}
        columns={columns}
        actions={actions}
        keyExtractor={(job) => job.id}
        isLoading={isLoading}
        loadingMessage="Loading jobs..."
        emptyMessage="No jobs created for this lead yet."
        error={error}
      />
      <CreateJobDialog
        lead={lead}
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onJobCreated={refetch}
      />
    </div>
  );
}
